import { SeriesOptionsType, Options, AxisTypeValue, DashStyleValue } from 'highcharts';
import {
  CompletionVsSendMetrics,
  CustomSeriesOptions,
  GoalAnalyticsMetricKey,
  SeriesMapping,
  MetricsKeys,
} from 'modules/iterableGoals/types';
import { fontSizes, colors, fontWeights } from 'ui/theme';
import {
  displayDateTz,
  DATE_FORMAT_TRUNC_YEAR,
  DATE_FORMAT_MONTH_DAY,
  DATE_FORMAT_DAY_NUMBERIC,
  MILLISECONDS_IN_DAY,
} from 'utils/date';
import {
  CHART_X_AXIS_TITLE_MARGIN,
  CHART_Y_AXIS_TITLE_OFFSET,
  CHART_AXIS_TITLE_SIZE,
  chartAxisLabelBaseStyles,
} from '../constants/common';
import { currencyLabelFormatter, pointFormatter } from './common';

export const getChartDateGroupSize = (numberOfDataInstances: number) =>
  Math.round(numberOfDataInstances / 5);

const seriesCategoryMapping = (getTranslation: (key: string) => string): SeriesMapping => ({
  [GoalAnalyticsMetricKey.REVENUE]: {
    name: getTranslation('labels.goalConversionsVsSends.goalConversions.revenue'),
    color: colors.cyan50,
  },
  [GoalAnalyticsMetricKey.TOTAL_GOAL_CONVERSIONS]: {
    name: getTranslation('labels.goalConversionsVsSends.goalConversions.event'),
    color: colors.cyan50,
  },
  [GoalAnalyticsMetricKey.TOTAL_SENT]: {
    name: getTranslation('labels.goalConversionsVsSends.campaignSends'),
    color: colors.cyan100,
  },
});

const metricsKeys: MetricsKeys = [
  GoalAnalyticsMetricKey.TOTAL_SENT,
  GoalAnalyticsMetricKey.TOTAL_GOAL_CONVERSIONS,
  GoalAnalyticsMetricKey.REVENUE,
];

export const getDateFormat = (range: number, isEndDate = false): string => {
  if (range <= 30 && isEndDate) return DATE_FORMAT_DAY_NUMBERIC;
  if (range > 120) return DATE_FORMAT_TRUNC_YEAR;

  return DATE_FORMAT_MONTH_DAY;
};

export const generateSeries = (
  metrics: CompletionVsSendMetrics,
  translation: (key: string) => string,
  isRevenue?: boolean,
): CustomSeriesOptions[] => {
  const buckets = metrics?.[GoalAnalyticsMetricKey.TOTAL_SENT]?.buckets || [];
  const numberOfDataInstances = buckets.length;
  const dataInstanceTimeDifferenceInDays =
    numberOfDataInstances > 1
      ? Math.abs(buckets[1].time - buckets[0].time) / MILLISECONDS_IN_DAY
      : 1;
  const dateGroupSize =
    dataInstanceTimeDifferenceInDays < 0.5
      ? numberOfDataInstances
      : getChartDateGroupSize(numberOfDataInstances);
  const dateGroupSizeInDays = dateGroupSize * dataInstanceTimeDifferenceInDays;

  return metricsKeys.reduce<CustomSeriesOptions[]>((acc, key) => {
    const seriesData = [];
    const categories = [];
    const scopedBuckets = metrics[key]?.buckets || [];
    const sortedScopedBuckets = [...scopedBuckets].sort((a, b) => a.time - b.time);

    while (sortedScopedBuckets.length) {
      const dataSet = sortedScopedBuckets.splice(0, dateGroupSize);
      const endDateAdjustment =
        dataInstanceTimeDifferenceInDays > 1
          ? (dataInstanceTimeDifferenceInDays - 1) * MILLISECONDS_IN_DAY
          : 0;
      const endDateInMilliseconds = sortedScopedBuckets.length
        ? sortedScopedBuckets[0].time - MILLISECONDS_IN_DAY
        : dataSet[dataSet.length - 1].time + endDateAdjustment;
      const shouldDisplayGroupStartAndEnd =
        (dateGroupSize >= 2 || dataInstanceTimeDifferenceInDays >= 14) &&
        endDateInMilliseconds - dataSet[0].time >= MILLISECONDS_IN_DAY;

      const startDate = displayDateTz(dataSet[0].time, getDateFormat(dateGroupSizeInDays));
      const endDate = displayDateTz(
        endDateInMilliseconds,
        getDateFormat(dateGroupSizeInDays, true),
      );

      categories.push(shouldDisplayGroupStartAndEnd ? `${startDate} - ${endDate}` : startDate);
      seriesData.push(dataSet.reduce((sum, { value }) => value + sum, 0));
    }

    const datum: SeriesOptionsType = {
      type: 'column',
      data: seriesData,
      tooltip: {
        valueDecimals: 0,
      },
      ...seriesCategoryMapping(translation)[key],
    };
    const hasData = seriesData.some((value) => value > 0);

    if (isRevenue) {
      const isNotTotalConversionsSeries = key !== GoalAnalyticsMetricKey.TOTAL_GOAL_CONVERSIONS;
      datum.yAxis = key === GoalAnalyticsMetricKey.REVENUE ? 1 : 0;
      datum.visible = isNotTotalConversionsSeries && hasData;
      datum.showInLegend = isNotTotalConversionsSeries && hasData;
    } else {
      const isNotRevenueSeries = key !== GoalAnalyticsMetricKey.REVENUE;
      datum.yAxis = 0;
      datum.visible = isNotRevenueSeries && hasData;
      datum.showInLegend = isNotRevenueSeries && hasData;
    }

    acc.push({
      ...datum,
      _categories: categories,
    });

    return acc;
  }, []);
};

export const getGoalConversionsVsSendsChartOptions = (
  series: CustomSeriesOptions[],
  getTranslation: (key: string) => string,
  isRevenue: boolean,
): Options => ({
  title: {
    text: undefined,
  },
  credits: {
    enabled: false,
  },
  exporting: {
    enabled: false,
  },
  chart: {
    type: 'column',
    marginLeft: isRevenue ? undefined : 65,
    marginRight: isRevenue ? undefined : 5,
    spacingBottom: 30,
  },
  legend: {
    margin: 10,
    itemStyle: {
      color: colors.textSecondary,
      fontSize: fontSizes.s16,
      fontWeight: fontWeights.regular.toString(),
    },
  },
  yAxis: [
    {
      type: 'linear' as AxisTypeValue,
      title: {
        text: getTranslation(`labels.goalConversionsVsSends.yAxis.event`),
        offset: CHART_Y_AXIS_TITLE_OFFSET,
        style: {
          fontSize: CHART_AXIS_TITLE_SIZE,
        },
      },
      labels: {
        style: {
          ...chartAxisLabelBaseStyles,
        },
      },
      lineWidth: 1,
      lineColor: colors.grey15,
      gridLineDashStyle: 'ShortDash' as DashStyleValue,
    },
    {
      type: 'linear' as AxisTypeValue,
      lineWidth: 1,
      lineColor: colors.grey15,
      title: {
        text: getTranslation('labels.goalConversionsVsSends.yAxis.revenue'),
        style: {
          fontSize: CHART_AXIS_TITLE_SIZE,
        },
      },
      labels: {
        formatter: currencyLabelFormatter,
        style: {
          ...chartAxisLabelBaseStyles,
        },
      },
      opposite: true,
    },
  ].filter(({ opposite }) => !opposite || isRevenue),
  tooltip: {
    shared: true,
    backgroundColor: colors.black,
    borderColor: colors.black,
    borderRadius: 8,
    headerFormat: `<span style="font-size: ${fontSizes.s12}">{point.key}</span><br/>`,
    pointFormatter: pointFormatter(isRevenue),
    style: {
      color: colors.white,
      fontSize: fontSizes.s12,
    },
  },
  plotOptions: {
    column: {
      groupPadding: 0.32,
      pointPadding: 0.1,
    },
  },
  xAxis: {
    categories: series[0]?._categories,
    title: {
      text: getTranslation('labels.goalConversionsVsSends.xAxis'),
      margin: CHART_X_AXIS_TITLE_MARGIN,
      style: {
        fontSize: CHART_AXIS_TITLE_SIZE,
      },
    },
    type: 'category' as AxisTypeValue,
    minPadding: 0.2,
    dateTimeLabelFormats: {
      week: '%b %e',
      day: '%b %e',
    },
    labels: {
      y: 22,
      style: {
        ...chartAxisLabelBaseStyles,
      },
    },
  },
  series: series as CustomSeriesOptions[],
});
